// nodeTypeRelatedLists.js
//
// Helpers for type `suggestedChildren` / related-type configuration.
// Supports legacy string keys and rich entries:
//   { type: 'PURCHASE_ORDER', alsoShowTabs: ['PURCHASE_ORDER_ITEM'] }
//   { type: 'TASK', showInTab: false, showInAttachMenu: false }

/**
 * normalizeTypeKey
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeTypeKey(value) {
  return String(value || '').trim();
}

/**
 * readRelatedEntryTypeKey
 *
 * @param {*} entry
 * @returns {string}
 */
function readRelatedEntryTypeKey(entry) {
  if (typeof entry === 'string') return normalizeTypeKey(entry);
  if (entry && typeof entry === 'object') {
    return normalizeTypeKey(entry.type ?? entry.key ?? entry.nodeType ?? entry.id);
  }
  return '';
}

/**
 * normalizeAlsoShowTabs
 *
 * @param {*} value
 * @param {string} [selfTypeKey]
 * @returns {string[]}
 */
export function normalizeAlsoShowTabs(value, selfTypeKey = '') {
  if (!Array.isArray(value)) return [];
  const self = normalizeTypeKey(selfTypeKey);
  const seen = new Set();
  const next = [];
  value.forEach((entry) => {
    const key = normalizeTypeKey(entry);
    if (!key || key === self || seen.has(key)) return;
    seen.add(key);
    next.push(key);
  });
  return next;
}

/**
 * readRelatedVisibilityFlag
 *
 * Defaults to true when omitted (legacy strings / old objects).
 *
 * @param {*} entry
 * @param {string} key
 * @returns {boolean}
 */
function readRelatedVisibilityFlag(entry, key) {
  if (!entry || typeof entry !== 'object') return true;
  if (!(key in entry)) return true;
  return entry[key] !== false;
}

/**
 * normalizeRelatedEntries
 *
 * Normalizes suggestedChildren into rich editor/runtime entries.
 *
 * @param {*} value
 * @returns {{
 *   type: string,
 *   alsoShowTabs: string[],
 *   showInTab: boolean,
 *   showInAttachMenu: boolean,
 * }[]}
 */
export function normalizeRelatedEntries(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  const next = [];

  value.forEach((entry) => {
    const type = readRelatedEntryTypeKey(entry);
    if (!type || seen.has(type)) return;
    seen.add(type);
    const alsoShowTabs = entry && typeof entry === 'object'
      ? normalizeAlsoShowTabs(entry.alsoShowTabs ?? entry.alsoShowTabTypes, type)
      : [];
    next.push({
      type,
      alsoShowTabs,
      showInTab: readRelatedVisibilityFlag(entry, 'showInTab'),
      showInAttachMenu: readRelatedVisibilityFlag(entry, 'showInAttachMenu'),
    });
  });

  return next;
}

/**
 * compactRelatedEntries
 *
 * Persist shape: plain string when defaults + no alsoShowTabs; object otherwise.
 * Omits true visibility flags; always writes explicit false.
 *
 * @param {*} value
 * @returns {(string|{
 *   type: string,
 *   alsoShowTabs?: string[],
 *   showInTab?: boolean,
 *   showInAttachMenu?: boolean,
 * })[]}
 */
export function compactRelatedEntries(value) {
  return normalizeRelatedEntries(value).map((entry) => {
    const needsObject = entry.alsoShowTabs.length > 0
      || entry.showInTab === false
      || entry.showInAttachMenu === false;
    if (!needsObject) return entry.type;

    const compacted = { type: entry.type };
    if (entry.alsoShowTabs.length) {
      compacted.alsoShowTabs = entry.alsoShowTabs;
    }
    if (entry.showInTab === false) {
      compacted.showInTab = false;
    }
    if (entry.showInAttachMenu === false) {
      compacted.showInAttachMenu = false;
    }
    return compacted;
  });
}

/**
 * normalizeTypeKeyList
 *
 * Normalizes a suggestedChildren list into unique N1 type keys
 * (ignores alsoShowTabs — those are not direct add targets).
 * Includes types regardless of showInTab / showInAttachMenu.
 *
 * @param {*} value - Raw list value from a type doc or draft state.
 * @returns {string[]} Clean type keys.
 */
export function normalizeTypeKeyList(value) {
  return normalizeRelatedEntries(value).map((entry) => entry.type);
}

/**
 * listAttachMenuTypeKeys
 *
 * N1 type keys that should appear in Attach / Add Related menus.
 *
 * @param {*} value
 * @returns {string[]}
 */
export function listAttachMenuTypeKeys(value) {
  return normalizeRelatedEntries(value)
    .filter((entry) => entry.showInAttachMenu !== false)
    .map((entry) => entry.type);
}

/**
 * listTabHiddenRelatedTypeKeys
 *
 * N1 type keys explicitly hidden from related tabs / hamburger
 * (`showInTab: false`). Orphan child types not in suggestedChildren
 * are not included — callers may still show those.
 *
 * @param {*} value
 * @returns {string[]}
 */
export function listTabHiddenRelatedTypeKeys(value) {
  return normalizeRelatedEntries(value)
    .filter((entry) => entry.showInTab === false)
    .map((entry) => entry.type);
}

/**
 * expandRelatedTypeOrder
 *
 * Tab order: each N1 type with showInTab, then its opted-in N2 tab types.
 * alsoShowTabs still emit even when the N1 type itself is tab-hidden.
 *
 * @param {*} value
 * @returns {string[]}
 */
export function expandRelatedTypeOrder(value) {
  const entries = normalizeRelatedEntries(value);
  const seen = new Set();
  const next = [];

  entries.forEach((entry) => {
    if (entry.showInTab !== false && !seen.has(entry.type)) {
      seen.add(entry.type);
      next.push(entry.type);
    }
    entry.alsoShowTabs.forEach((tabType) => {
      if (seen.has(tabType)) return;
      seen.add(tabType);
      next.push(tabType);
    });
  });

  return next;
}

/**
 * getAlsoShowTabPaths
 *
 * Configured N2 paths: host → throughType → tabType.
 *
 * @param {*} value
 * @returns {{ throughType: string, tabType: string }[]}
 */
export function getAlsoShowTabPaths(value) {
  const paths = [];
  normalizeRelatedEntries(value).forEach((entry) => {
    entry.alsoShowTabs.forEach((tabType) => {
      paths.push({
        throughType: entry.type,
        tabType,
      });
    });
  });
  return paths;
}

/**
 * typeKeyListsEqual
 *
 * Compares two type-key lists for exact ordered equality after normalization.
 *
 * @param {string[]} left - First list.
 * @param {string[]} right - Second list.
 * @returns {boolean} Whether both lists match.
 */
export function typeKeyListsEqual(left, right) {
  const a = normalizeTypeKeyList(left);
  const b = normalizeTypeKeyList(right);
  if (a.length !== b.length) return false;
  return a.every((key, index) => key === b[index]);
}

/**
 * relatedEntriesEqual
 *
 * Compares rich related-entry lists (type + alsoShowTabs + visibility flags).
 *
 * @param {*} left
 * @param {*} right
 * @returns {boolean}
 */
export function relatedEntriesEqual(left, right) {
  const a = normalizeRelatedEntries(left);
  const b = normalizeRelatedEntries(right);
  if (a.length !== b.length) return false;
  return a.every((entry, index) => {
    const other = b[index];
    if (entry.type !== other.type) return false;
    if (entry.showInTab !== other.showInTab) return false;
    if (entry.showInAttachMenu !== other.showInAttachMenu) return false;
    if (entry.alsoShowTabs.length !== other.alsoShowTabs.length) return false;
    return entry.alsoShowTabs.every((key, tabIndex) => key === other.alsoShowTabs[tabIndex]);
  });
}

/**
 * relatedDraftListsEqual
 *
 * Compares a draft list (which may include empty slots) against a saved list.
 *
 * @param {*} draft - Draft list from the editor.
 * @param {*} saved - Saved list from the type definition.
 * @returns {boolean} Whether the draft matches the saved list.
 */
export function relatedDraftListsEqual(draft, saved) {
  const draftList = Array.isArray(draft) ? draft : [];
  const savedEntries = normalizeRelatedEntries(saved);

  // Allow empty type slots only in draft UI; treat them as unequal if present.
  const draftHasEmpty = draftList.some((entry) => !readRelatedEntryTypeKey(entry));
  if (draftHasEmpty) return false;

  return relatedEntriesEqual(draftList, savedEntries);
}
